import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ChevronRight, Phone, Clock, MapPin, Shield, Car, Heart, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import cemeteryHero from "@/assets/blog/fort-sam-houston-cemetery-hero.jpg";

const FortSamHoustonFuneralGuide = () => {
  const tableOfContents = [
    { id: "base-access", label: "Base Access Requirements" },
    { id: "committal-shelter", label: "Committal Shelter Rule" },
    { id: "procession-assembly", label: "Procession Assembly" },
    { id: "transportation", label: "Professional Transportation" },
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <>
      <Helmet>
        <title>Fort Sam Houston National Cemetery Funeral Guide | Rick's Black Car Service</title>
        <meta 
          name="description" 
          content="Essential guidelines for base access, committal shelters, and etiquette for funerals at Fort Sam Houston National Cemetery in San Antonio." 
        />
        <meta 
          name="keywords" 
          content="Fort Sam Houston funeral rules, San Antonio funeral car service, JBSA base access funeral, military cemetery transportation" 
        />
        <link rel="canonical" href="https://ricksblackcarservicesantonio.com/blog/fort-sam-houston-funeral-guest-guidelines" />
        
        {/* Open Graph */}
        <meta property="og:title" content="A Guide for Guests: Attending a Funeral at Fort Sam Houston National Cemetery" />
        <meta property="og:description" content="Essential guidelines for base access, committal shelters, and etiquette for funerals at Fort Sam Houston National Cemetery." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://ricksblackcarservicesantonio.com/blog/fort-sam-houston-funeral-guest-guidelines" />
        
        {/* BlogPosting Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": "A Guide for Guests: Attending a Funeral Service at Fort Sam Houston National Cemetery",
            "description": "Essential guidelines for base access, committal shelters, and etiquette for funerals at Fort Sam Houston National Cemetery in San Antonio.",
            "author": {
              "@type": "Person",
              "name": "Rick Ruiz-Estrada"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Rick's Black Car Service",
              "logo": {
                "@type": "ImageObject",
                "url": "https://ricksblackcarservicesantonio.com/og-image.jpg"
              }
            },
            "datePublished": "2025-01-05",
            "dateModified": "2025-01-05",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://ricksblackcarservicesantonio.com/blog/fort-sam-houston-funeral-guest-guidelines"
            },
            "image": "https://ricksblackcarservicesantonio.com/images/fort-sam-houston-cemetery.jpg",
            "keywords": "Fort Sam Houston funeral rules, San Antonio funeral car service, JBSA base access funeral, military cemetery transportation"
          })}
        </script>
      </Helmet>

      <Navbar />
      
      <main className="min-h-screen bg-background pt-20">
        {/* Hero Section */}
        <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/70 to-background z-10" />
          <img 
            src={cemeteryHero}
            alt="Fort Sam Houston National Cemetery Funeral Procession San Antonio"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: "grayscale(20%)" }}
          />
          
          <div className="relative z-20 container mx-auto px-4 text-center">
            <motion.div {...fadeInUp}>
              {/* Breadcrumb */}
              <nav className="flex items-center justify-center space-x-2 text-sm text-muted-foreground mb-6">
                <Link to="/" className="hover:text-primary transition-colors">Home</Link>
                <ChevronRight className="w-4 h-4" />
                <Link to="/blog" className="hover:text-primary transition-colors">Resources</Link>
                <ChevronRight className="w-4 h-4" />
                <span className="text-foreground">Fort Sam Houston Guide</span>
              </nav>
              
              <h1 className="font-playfair text-3xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 leading-tight">
                A Guide for Guests
              </h1>
              <p className="text-xl md:text-2xl text-primary font-light max-w-3xl mx-auto">
                Attending a Funeral Service at Fort Sam Houston National Cemetery
              </p>
              <p className="text-muted-foreground mt-4 max-w-2xl mx-auto font-serif text-lg">
                Navigating base protocols, timing, and etiquette when paying your final respects to a veteran in San Antonio.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Main Content Grid */}
        <div className="container mx-auto px-4 py-12 lg:py-16">
          <div className="grid lg:grid-cols-[1fr_320px] gap-12">
            
            {/* Article Content */}
            <article className="prose prose-lg prose-invert max-w-none">
              {/* Quick Links TOC */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="not-prose bg-muted/30 border border-border/50 rounded-xl p-6 mb-10"
              >
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  Quick Links
                </h2>
                <ul className="space-y-2">
                  {tableOfContents.map((item) => (
                    <li key={item.id}>
                      <a 
                        href={`#${item.id}`}
                        className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2"
                      >
                        <ChevronRight className="w-4 h-4" />
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Introduction */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="font-serif text-lg leading-relaxed text-foreground/90"
              >
                <p className="text-xl leading-relaxed mb-6">
                  It is a profound honor to lay a loved one to rest at Fort Sam Houston National Cemetery. The rows of white headstones under the Texas oaks serve as a powerful reminder of service and sacrifice.
                </p>
                
                <p className="mb-6">
                  However, for family and guests arriving from off-base, attending a service here presents unique logistical challenges. Fort Sam Houston is an active military installation, meaning access is strictly controlled. Navigating security protocols while grieving can feel overwhelming.
                </p>
                
                <p className="mb-10 text-muted-foreground italic">
                  To ensure a dignified and stress-free experience for your family, here are the essential guidelines for attending a service at the National Cemetery.
                </p>
              </motion.div>

              {/* Section 1: Base Access */}
              <motion.section 
                id="base-access"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="scroll-mt-24"
              >
                <h2 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mb-6 flex items-center gap-3">
                  <span className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/20 text-primary text-lg font-bold">1</span>
                  Base Access is the Biggest Hurdle
                </h2>
                
                <div className="font-serif text-lg leading-relaxed text-foreground/90 space-y-4">
                  <p>
                    Unlike a civilian cemetery, you cannot simply drive in. <strong>Every adult (18+) in every vehicle must have valid, government-issued photo identification.</strong>
                  </p>
                  
                  <ul className="space-y-3 my-6">
                    <li className="flex items-start gap-3">
                      <Shield className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <span>
                        <strong>If someone in the car has a DoD ID (Military/Dependent/Retiree):</strong> They can escort the vehicle through the gate.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <span>
                        <strong>If no one has a DoD ID:</strong> You must stop at the Visitor Control Center located at the Walters Street gate or the specific commercial gate designated for procession entry. You must obtain a temporary pass. Please allow extra time for this process—lines can be long.
                      </span>
                    </li>
                  </ul>
                  
                  <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 my-6">
                    <p className="text-destructive font-semibold flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      Important
                    </p>
                    <p className="text-foreground/80 mt-2">
                      Do not bring any firearms or weapons onto the installation, regardless of concealed carry permits. Your vehicle is subject to search.
                    </p>
                  </div>
                </div>
              </motion.section>

              {/* Section 2: Committal Shelter */}
              <motion.section 
                id="committal-shelter"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="mt-12 scroll-mt-24"
              >
                <h2 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mb-6 flex items-center gap-3">
                  <span className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/20 text-primary text-lg font-bold">2</span>
                  The "Committal Shelter" Rule
                </h2>
                
                <div className="font-serif text-lg leading-relaxed text-foreground/90 space-y-4">
                  <p>
                    A common misconception is that services take place at the final gravesite. At Fort Sam Houston, to accommodate the high volume of daily services with military precision, <strong>the service takes place at a designated "Committal Shelter."</strong>
                  </p>
                  
                  <p>
                    These are open-air pavilions where the family gathers, military honors are rendered (Taps, flag folding), and clergy speak.
                  </p>
                  
                  <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 my-6">
                    <p className="text-primary font-semibold flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      Timing Note
                    </p>
                    <p className="text-foreground/80 mt-2">
                      Services are strictly timed, usually lasting exactly 20 to 30 minutes. <strong>Punctuality is vital.</strong>
                    </p>
                  </div>
                </div>
              </motion.section>

              {/* Section 3: Procession Assembly */}
              <motion.section 
                id="procession-assembly"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="mt-12 scroll-mt-24"
              >
                <h2 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mb-6 flex items-center gap-3">
                  <span className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/20 text-primary text-lg font-bold">3</span>
                  The Procession Assembly Area
                </h2>
                
                <div className="font-serif text-lg leading-relaxed text-foreground/90 space-y-4">
                  <p>
                    <strong>Do not drive directly to the shelter.</strong> Your funeral director will give you a specific Assembly Area number. You must arrive at this staging area at least 15–20 minutes before the scheduled service time.
                  </p>
                  
                  <p>
                    The cemetery representative will then lead the procession from the assembly area to the correct committal shelter.
                  </p>
                </div>
              </motion.section>

              {/* CTA Box - Mid Article */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="not-prose my-12 bg-gradient-to-br from-slate-800 via-slate-900 to-background border border-primary/30 rounded-2xl p-8 text-center"
              >
                <Heart className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="font-playfair text-2xl font-bold text-foreground mb-3">
                  Simplify Your Visit to Fort Sam Houston
                </h3>
                <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                  We have authorized JBSA access to bypass visitor gate lines. Let us handle the logistics while you focus on your family.
                </p>
                <Button asChild size="lg" className="btn-luxury">
                  <Link to="/services/funeral-transportation">
                    <Car className="w-5 h-5 mr-2" />
                    Book Family Transportation
                  </Link>
                </Button>
              </motion.div>

              {/* Section 4: Transportation Solution */}
              <motion.section 
                id="transportation"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="mt-12 scroll-mt-24"
              >
                <h2 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mb-6">
                  Removing the Burden of Driving
                </h2>
                
                <div className="font-serif text-lg leading-relaxed text-foreground/90 space-y-4">
                  <p>
                    When you are grieving, the last thing you should worry about is navigating base security gates, finding the correct assembly area, or driving in a tight procession.
                  </p>
                  
                  <p>
                    At <strong>Rick's Black Car Service</strong>, we offer dignified, private funeral transportation for immediate family members.
                  </p>
                  
                  <div className="bg-primary/10 border border-primary/30 rounded-xl p-6 my-8">
                    <h3 className="font-playfair text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                      <Shield className="w-6 h-6 text-primary" />
                      The Advantage of JBSA Access
                    </h3>
                    <p className="text-foreground/90">
                      Because our chauffeurs hold authorized Joint Base San Antonio (JBSA) commercial access credentials, we can <strong>bypass the Visitor Control Center lines</strong>. We transport your family directly from the funeral home onto the base and to the assembly area seamlessly.
                    </p>
                  </div>
                  
                  <p className="text-lg italic text-muted-foreground">
                    Allow us to handle the logistics and the driving, so your family can focus on supporting one another during this difficult time.
                  </p>
                </div>
              </motion.section>

              {/* Final CTA Box */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="not-prose mt-16 bg-gradient-to-br from-slate-800 via-slate-900 to-background border border-primary/30 rounded-2xl p-8"
              >
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="font-playfair text-2xl font-bold text-foreground mb-2">
                      Ready to Book Transportation?
                    </h3>
                    <p className="text-muted-foreground">
                      Call us directly or visit our funeral services page to learn more about how we can support your family.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button asChild variant="outline" size="lg" className="border-primary text-primary hover:bg-primary/10">
                      <a href="tel:+12102967409">
                        <Phone className="w-5 h-5 mr-2" />
                        210-296-7409
                      </a>
                    </Button>
                    <Button asChild size="lg" className="btn-luxury">
                      <Link to="/services/funeral-transportation">
                        Learn More
                      </Link>
                    </Button>
                  </div>
                </div>
              </motion.div>

              {/* Back to Blog */}
              <div className="mt-12 pt-8 border-t border-border/50">
                <Link 
                  to="/blog" 
                  className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Resources
                </Link>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="lg:sticky lg:top-28 lg:self-start space-y-6">
              {/* Related Services */}
              <Card className="bg-muted/30 border-border/50">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-foreground mb-4">Related Services</h3>
                  <ul className="space-y-3">
                    <li>
                      <Link 
                        to="/services/funeral-transportation"
                        className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Heart className="w-4 h-4 text-primary" />
                        Funeral Transportation
                      </Link>
                    </li>
                    <li>
                      <Link 
                        to="/services/airport-transportation"
                        className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Car className="w-4 h-4 text-primary" />
                        Airport Transfers
                      </Link>
                    </li>
                    <li>
                      <Link 
                        to="/jbsa-military-transportation"
                        className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Shield className="w-4 h-4 text-primary" />
                        JBSA Base Access
                      </Link>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Contact Card */}
              <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/30">
                <CardContent className="p-6 text-center">
                  <Phone className="w-8 h-8 text-primary mx-auto mb-3" />
                  <h3 className="font-semibold text-foreground mb-2">Need Assistance?</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    We're available 24/7 to help coordinate your family's transportation.
                  </p>
                  <Button asChild variant="outline" className="w-full border-primary text-primary hover:bg-primary/10">
                    <a href="tel:+12102967409">
                      Call 210-296-7409
                    </a>
                  </Button>
                </CardContent>
              </Card>

              {/* Author Card */}
              <Card className="bg-muted/30 border-border/50">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-foreground mb-3">About the Author</h3>
                  <p className="text-sm text-muted-foreground">
                    <strong>Rick Ruiz-Estrada</strong> is a 22-year Army Veteran and founder of Rick's Black Car Service. With authorized JBSA access credentials, he provides dignified transportation for military families throughout San Antonio.
                  </p>
                </CardContent>
              </Card>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default FortSamHoustonFuneralGuide;
